import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrum } from "../../../components/Layout/breadcrum";
import Checkbox from "../../../components/CheckBox";
import OneButton from "../../../components/Button/OneButton";
import { toast } from "react-hot-toast";
import { POST } from "../../../utils/webService";
import {
  GET_INVOICE_SETTING,
  SET_INVOICE_SETTING,
  PREVIEW_INVOICE_SETTING,
} from "../../../utils/ApiUrls";
import { Spinner } from "../../../components/Spinner";

interface InvoiceSettingsState {
  hideSellerPhone: boolean;
  hideSellerEmail: boolean;
  hideBuyerPhone: boolean;
  hideBuyerEmail: boolean;
  hideBillingAddressPhone: boolean;
  hideBillingAddressEmail: boolean;
}

const defaultSettings: InvoiceSettingsState = {
  hideSellerPhone: false,
  hideSellerEmail: false,
  hideBuyerPhone: false,
  hideBuyerEmail: false,
  hideBillingAddressPhone: false,
  hideBillingAddressEmail: false,
};

const mapApiResponseToSettings = (data: any): InvoiceSettingsState => {
  if (!data) return defaultSettings;
  return {
    hideSellerPhone: Boolean(data.hideSellerPhone ?? defaultSettings.hideSellerPhone),
    hideSellerEmail: Boolean(data.hideSellerEmail ?? defaultSettings.hideSellerEmail),
    hideBuyerPhone: Boolean(data.hideBuyerPhone ?? defaultSettings.hideBuyerPhone),
    hideBuyerEmail: Boolean(data.hideBuyerEmail ?? defaultSettings.hideBuyerEmail),
    hideBillingAddressPhone: Boolean(
      data.hideBillingAddressPhone ??
        data.hideBillingPhone ??
        defaultSettings.hideBillingAddressPhone
    ),
    hideBillingAddressEmail: Boolean(
      data.hideBillingAddressEmail ??
        data.hideBillingEmail ??
        defaultSettings.hideBillingAddressEmail
    ),
  };
};

const InvoiceSettings: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<InvoiceSettingsState>(defaultSettings);
  const [originalSettings, setOriginalSettings] = useState<InvoiceSettingsState>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  const hasChanges =
    settings.hideSellerPhone !== originalSettings.hideSellerPhone ||
    settings.hideSellerEmail !== originalSettings.hideSellerEmail ||
    settings.hideBuyerPhone !== originalSettings.hideBuyerPhone ||
    settings.hideBuyerEmail !== originalSettings.hideBuyerEmail ||
    settings.hideBillingAddressPhone !== originalSettings.hideBillingAddressPhone ||
    settings.hideBillingAddressEmail !== originalSettings.hideBillingAddressEmail;

  const fetchSettings = async () => {
    try {
      setFetchLoading(true);
      const response: any = await POST(GET_INVOICE_SETTING, {});
      const apiData = response?.data?.data ?? response?.data;
      const mappedSettings = mapApiResponseToSettings(apiData);
      setSettings(mappedSettings);
      setOriginalSettings(mappedSettings);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load invoice settings");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    if (!hasChanges) return;
    try {
      setLoading(true);
      const invoiceSetting = {
        hideSellerPhone: settings.hideSellerPhone,
        hideSellerEmail: settings.hideSellerEmail,
        hideBuyerPhone: settings.hideBuyerPhone,
        hideBuyerEmail: settings.hideBuyerEmail,
        hideBillingPhone: settings.hideBillingAddressPhone,
        hideBillingEmail: settings.hideBillingAddressEmail,
      };
      const { data }: any = await POST(SET_INVOICE_SETTING, { invoiceSetting });
      if (data?.success) {
        toast.success(data?.message ?? "Invoice settings saved successfully");
        setOriginalSettings(settings);
        navigate("/settings");
      } else {
        toast.error(data?.message ?? "Failed to save invoice settings");
      }
    } catch (err) {
      toast.error("Failed to save invoice settings");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      const invoiceSetting = {
        hideSellerPhone: settings.hideSellerPhone,
        hideSellerEmail: settings.hideSellerEmail,
        hideBuyerPhone: settings.hideBuyerPhone,
        hideBuyerEmail: settings.hideBuyerEmail,
        hideBillingPhone: settings.hideBillingAddressPhone,
        hideBillingEmail: settings.hideBillingAddressEmail,
      };
      const response: any = await POST(
        PREVIEW_INVOICE_SETTING,
        { invoiceSetting },
        {
          responseType: "blob",
          headers: { "Content-Type": "application/json" },
        }
      );
      const blobData = response?.data ?? response;
      const blob = new Blob([blobData], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Failed to preview invoice");
    } finally {
      setPreviewLoading(false);
    }
  };

  const checkboxOptions = [
    {
      key: "hideSellerPhone" as const,
      label: "Hide Seller's Phone Number",
      description: "If checked, your phone number will be hidden from the invoice.",
    },
    {
      key: "hideSellerEmail" as const,
      label: "Hide Seller's Email ID",
      description: "If checked, your email ID will be hidden from the invoice.",
    },
    {
      key: "hideBuyerPhone" as const,
      label: "Hide Buyer's Phone Number",
      description:
        "If checked, the buyer's phone number will be hidden from the invoice.",
    },
    {
      key: "hideBuyerEmail" as const,
      label: "Hide Buyer's Email ID",
      description:
        "If checked, the buyer's email ID will be hidden from the invoice.",
    },
    {
      key: "hideBillingAddressPhone" as const,
      label: "Hide Billing Address Phone Number",
      description:
        "If checked, the billing address phone number will be hidden from the invoice.",
    },
    {
      key: "hideBillingAddressEmail" as const,
      label: "Hide Billing Address Email ID",
      description:
        "If checked, the billing address email ID will be hidden from the invoice.",
    },
  ];

  if (fetchLoading) {
    return (
      <>
        <div>
          <Breadcrum label="Invoice Settings" />
        </div>
        <div className="flex justify-center items-center min-h-[200px]">
          <Spinner />
        </div>
      </>
    );
  }

  return (
    <>
      <div>
        <Breadcrum label="Invoice Settings" />
      </div>
      <div className="!pb-[50px] mx-4">
        <h1 className="font-Lato text-[22px] font-semibold leading-7 text-[#1C1C1C] mt-[10px]">
          Invoice Settings
        </h1>

        <div
          className="border-[1px] border-[#E8E8E8] rounded-lg p-5 mt-4"
          style={{
            boxShadow:
              "0px 0px 0px 0px rgba(133, 133, 133, 0.05), 0px 6px 13px 0px rgba(133, 133, 133, 0.05)",
          }}
        >
          <h2 className="font-Lato text-lg font-semibold leading-7 text-[#1C1C1C]">
            Invoice Setting
          </h2>
          <p className="font-Open text-sm font-normal text-[#777777] mt-1 leading-5">
            Control which contact details appear on your invoices
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {checkboxOptions.map((option) => (
              <div key={option.key} className="flex flex-col gap-1">
                <Checkbox
                  style={{ accentColor: "black" }}
                  checkboxClassName="gap-x-2 !h-6"
                  label={option.label}
                  labelClassName="!font-Open !text-base !text-[#1C1C1C] !font-semibold !leading-[22px]"
                  onChange={(e: any) => {
                    setSettings((prev) => ({
                      ...prev,
                      [option.key]: e.value,
                    }));
                  }}
                  checked={settings[option.key]}
                />
                <p className="font-Open text-sm font-normal text-[#777777] leading-5 pl-7">
                  {option.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <footer className="w-full fixed lg:-ml-2 bottom-0 left-0 right-0">
          <div className="grid grid-cols-2 shadow-lg border-[1px] bg-[#FFFFFF] gap-[32px] p-[24px] rounded-tr-[24px] rounded-tl-[24px] fixed w-full bottom-0 lg:flex lg:justify-end lg:!w-[calc(100%-64px)] lg:!left-[32px]">
            <OneButton
              text={
                previewLoading ? (
                  <div className="flex">
                    <Spinner />
                  </div>
                ) : (
                  "Preview Invoice"
                )
              }
              className="flex justify-center items-center rounded-md w-full lg:w-[140px] h-9 p-2"
              variant="secondary"
              onClick={handlePreview}
              size="medium"
              disabled={previewLoading}
            />
            <OneButton
              text={loading ? "Saving..." : "Save"}
              className={`${
                loading || !hasChanges
                  ? "bg-white text-black"
                  : "bg-[#1C1C1C] text-[#FFFFFF]"
              } lg:w-[100px] !border-0`}
              variant={loading || !hasChanges ? "secondary" : "primary"}
              onClick={handleSave}
              size="medium"
              disabled={!hasChanges || loading}
            />
          </div>
        </footer>
      </div>
    </>
  );
};

export default InvoiceSettings;
